import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import { serverEvents, debounce } from '../lib/events.js';
import type { ServerEvent } from '../lib/events.js';

interface PackRecord {
  stock_id: string;
  pack_type: { code: string; name: string };
  pack_description: string;
  packsize: number;
  selling_price: number;
  membership_price: number;
  max_discount_percentage: number;
  percent_above_cost: number;
  barcode: string;
}

interface SearchRow {
  storeName: string;
  storeCode: string;
  hostAddress: string;
  id: string;
  code: string;
  name: string;
  description: string;
  imageUrl: string;
  thumbnailUrl: string;
  /** All thumbnail URLs for carousel (multi-image) */
  allImageUrls: Array<{ url: string; imageNo: number }>;
  price: number;
  membershipPrice: number;
  packRecords: PackRecord[];
  qoh: number;
  qohString: string;
  typeName: string;
}

/** Format a number as currency (MYR). */
function formatPrice(n: number): string {
  return n.toFixed(2);
}

type SortField = 'code' | 'name' | 'price' | 'qoh' | 'store';

@customElement('stock-search')
export class StockSearch extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }
    .search-fixed {
      flex-shrink: 0;
    }
    .search-bar {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 1rem;
    }
    input {
      flex: 1;
      min-height: 44px;
      padding: 10px 14px;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      font-size: 1rem;
      box-sizing: border-box;
      background: var(--color-surface);
      color: var(--color-text);
    }
    input:focus { outline: none; border-color: var(--color-primary); box-shadow: 0 0 0 3px rgba(26,115,232,0.16); }
    .btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 44px;
      padding: 0 20px;
      background: var(--color-primary);
      color: #fff;
      border: none;
      border-radius: 12px;
      font-family: inherit;
      font-size: 0.9rem;
      font-weight: 600;
      cursor: pointer;
      transition: filter 0.15s;
    }
    .btn:hover:not(:disabled) { filter: brightness(1.08); }
    .btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .summary {
      font-size: 0.85rem;
      color: var(--color-text-secondary);
      margin-bottom: 0.75rem;
    }
    .results-scroll {
      flex: 1;
      overflow-y: auto;
      min-height: 0;
      -webkit-overflow-scrolling: touch;
    }
    .tile-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      gap: 0.5rem;
    }
    .tile {
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: 14px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(15,35,80,0.08);
      display: flex;
      flex-direction: column;
      transition: box-shadow 0.15s;
    }
    .tile:hover { box-shadow: 0 6px 18px rgba(15,35,80,0.14); }

    /* ── Carousel ── */
    .carousel {
      position: relative;
      width: 100%;
      aspect-ratio: 1 / 1;
      background: var(--color-border);
      flex-shrink: 0;
      overflow: hidden;
    }
    .carousel-track {
      display: flex;
      width: 100%;
      height: 100%;
      transition: transform 0.35s ease;
    }
    .carousel-slide {
      min-width: 100%;
      height: 100%;
    }
    .carousel-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    .carousel-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--color-text-secondary);
      font-size: 0.75rem;
      user-select: none;
    }

    /* dots — wrapped in a dark pill for visibility on any background */
    .carousel-dots {
      position: absolute;
      bottom: 6px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 5px;
      z-index: 2;
      background: rgba(0,0,0,0.35);
      padding: 3px 8px;
      border-radius: 10px;
    }
    .carousel-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: rgba(255,255,255,0.55);
      border: 1px solid rgba(0,0,0,0.3);
      padding: 0;
      cursor: pointer;
      transition: background 0.2s, transform 0.2s;
    }
    .carousel-dot.active {
      background: #fff;
      border-color: rgba(0,0,0,0.5);
      transform: scale(1.35);
    }
    .carousel-counter {
      position: absolute;
      top: 6px;
      right: 8px;
      background: rgba(0,0,0,0.5);
      color: #fff;
      font-size: 0.65rem;
      padding: 2px 6px;
      border-radius: 8px;
      z-index: 2;
    }

    /* upload button (overlay on carousel) */
    .tile-upload-btn {
      position: absolute;
      bottom: 30px;
      right: 6px;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: rgba(0,0,0,0.55);
      color: #fff;
      border: none;
      font-size: 1.1rem;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.15s;
      z-index: 3;
    }
    .carousel:hover .tile-upload-btn {
      opacity: 1;
    }
    .tile-upload-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .tile-upload-spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 28px;
      height: 28px;
      border: 3px solid rgba(255,255,255,0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
      z-index: 4;
    }

    /* delete button */
    .tile-delete-btn {
      position: absolute;
      top: 6px;
      left: 6px;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      background: rgba(0,0,0,0.5);
      color: #fff;
      border: none;
      font-size: 0.85rem;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.15s;
      z-index: 3;
    }
    .carousel:hover .tile-delete-btn {
      opacity: 1;
    }
    .tile-delete-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    @keyframes spin {
      to { transform: translate(-50%, -50%) rotate(360deg); }
    }
    #upload-input {
      display: none;
    }

    .tile-body {
      padding: 0.5rem 0.6rem 0.6rem;
      flex: 1;
      min-width: 0;
    }
    .tile-name {
      font-weight: 600;
      font-size: 0.8rem;
      line-height: 1.25;
      margin-bottom: 0.15rem;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .tile-code {
      font-size: 0.68rem;
      color: var(--color-text-secondary);
      margin-bottom: 0.25rem;
    }
    .tile-details {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem 0.5rem;
      font-size: 0.72rem;
      margin-bottom: 0.2rem;
    }
    .tile-detail {
      display: flex;
      align-items: center;
      gap: 0.15rem;
    }
    .tile-detail-label {
      color: var(--color-text-secondary);
      font-size: 0.65rem;
    }
    .tile-detail-value {
      font-weight: 500;
    }
    .tile-price {
      color: var(--color-primary, #1a73e8);
      font-weight: 600;
      font-size: 0.82rem;
    }
    .tile-packs {
      margin-top: 0.2rem;
      display: flex;
      flex-wrap: wrap;
      gap: 0.2rem;
    }
    .tile-pack-tag {
      font-size: 0.62rem;
      background: var(--color-surface-hover, #f0f4ff);
      color: var(--color-primary, #1a73e8);
      padding: 1px 6px;
      border-radius: 10px;
      white-space: nowrap;
    }
    .tile-meta {
      margin-top: 0.3rem;
      font-size: 0.65rem;
      color: var(--color-text-secondary);
      display: flex;
      align-items: center;
      gap: 0.2rem;
    }
    .tile-meta svg {
      width: 12px;
      height: 12px;
    }
    .btn-add-cart {
      margin-top: 0.35rem;
      width: 100%;
      min-height: 36px;
      padding: 5px 8px;
      border: 1px solid var(--color-primary);
      border-radius: 10px;
      background: transparent;
      color: var(--color-primary);
      font-family: inherit;
      font-size: 0.78rem;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s;
    }
    .btn-add-cart:hover { background: #e8f0fe; }
    .btn-add-cart:disabled { border-color: var(--color-success); color: var(--color-success); background: #e6f4ea; cursor: default; }
    .no-results {
      padding: 2rem;
      text-align: center;
      color: var(--color-text-secondary);
    }

    /* ── Search bar wrapper (for positioning the dropdown) ── */
    .search-bar-wrapper {
      position: relative;
      flex: 1;
    }
    .search-bar-wrapper input {
      width: 100%;
      box-sizing: border-box;
    }

    /* ── Autocomplete suggestion dropdown ── */
    .suggestions-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      z-index: 50;
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-top: none;
      border-radius: 0 0 14px 14px;
      box-shadow: 0 12px 30px rgba(15,35,80,0.16);
      max-height: 260px;
      overflow-y: auto;
    }
    .suggestion-item {
      padding: 12px 14px;
      min-height: 44px;
      box-sizing: border-box;
      cursor: pointer;
      font-size: 0.9rem;
      color: var(--color-text-primary, #1a1a1a);
      border-bottom: 1px solid var(--color-border);
      transition: background 0.1s;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .suggestion-item:last-child {
      border-bottom: none;
    }
    .suggestion-item:hover,
    .suggestion-item.active {
      background: var(--color-surface-hover, #f0f4ff);
      color: var(--color-primary);
    }
    .suggestion-item .suggestion-icon {
      opacity: 0.4;
      flex-shrink: 0;
      width: 14px;
      height: 14px;
    }
    .suggestion-item .suggestion-text {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .suggestion-item .suggestion-hint {
      font-size: 0.7rem;
      opacity: 0.5;
      flex-shrink: 0;
    }

    /* ── View toggle button ── */
    .view-toggle {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      min-height: 44px;
      padding: 0 14px;
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: 12px;
      font-family: inherit;
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      color: var(--color-text-secondary);
      white-space: nowrap;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
    }
    .view-toggle:hover {
      background: var(--color-surface-hover, #f0f4ff);
      border-color: var(--color-primary);
      color: var(--color-primary);
    }
    .view-toggle svg {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }

    /* ── List view ── */
    .list-view {
      display: flex;
      flex-direction: column;
      gap: 0;
      border: 1px solid var(--color-border);
      border-radius: 14px;
      /* clip, NOT hidden: overflow:hidden would become the sticky scroll box and un-pin .list-header */
      overflow: clip;
    }

    /* ── List header row ── */
    .list-header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.45rem 0.75rem;
      background: var(--color-surface-hover, #f0f2f5);
      border-bottom: 2px solid var(--color-border);
      min-width: 0;
      position: sticky;
      top: 0;
      z-index: 2;
    }
    .list-header-cell {
      display: flex;
      align-items: center;
      gap: 0.2rem;
      font-size: 0.72rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--color-text-secondary);
      white-space: nowrap;
      user-select: none;
    }
    .list-header-cell.sortable {
      cursor: pointer;
      transition: color 0.15s;
    }
    .list-header-cell.sortable:hover {
      color: var(--color-primary);
    }
    .list-header-cell.active {
      color: var(--color-primary);
    }
    .sort-arrow {
      display: inline-flex;
      flex-direction: column;
      line-height: 0.6;
      font-size: 0.55rem;
      opacity: 0.4;
    }
    .sort-arrow.asc {
      opacity: 1;
    }
    .sort-arrow.desc {
      opacity: 1;
    }
    .list-header-code {
      min-width: 80px;
      max-width: 140px;
    }
    .list-header-name {
      flex: 1;
      min-width: 0;
    }
    .list-header-price {
      min-width: 70px;
      justify-content: flex-end;
    }
    .list-header-qoh {
      min-width: 50px;
      justify-content: flex-end;
    }
    .list-header-store {
      min-width: 90px;
      max-width: 130px;
      justify-content: flex-end;
    }

    /* ── List data rows ── */
    .list-item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.6rem 0.75rem;
      background: var(--color-surface);
      border-bottom: 1px solid var(--color-border);
      transition: background 0.1s;
      min-width: 0;
    }
    .list-item:last-child {
      border-bottom: none;
    }
    .list-item:hover {
      background: var(--color-surface-hover, #f8f9ff);
    }
    .list-item-code {
      font-weight: 600;
      font-size: 0.82rem;
      white-space: nowrap;
      min-width: 80px;
      max-width: 140px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .list-item-name {
      flex: 1;
      font-size: 0.82rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }
    .list-item-price {
      font-weight: 600;
      font-size: 0.82rem;
      color: var(--color-primary, #1a73e8);
      white-space: nowrap;
      min-width: 70px;
      text-align: right;
    }
    .list-item-qoh {
      font-size: 0.78rem;
      color: var(--color-text-secondary);
      white-space: nowrap;
      min-width: 50px;
      text-align: right;
    }
    .list-item-store {
      font-size: 0.72rem;
      color: var(--color-text-secondary);
      white-space: nowrap;
      min-width: 90px;
      max-width: 130px;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: right;
    }
    .list-item-packs {
      display: flex;
      gap: 0.2rem;
      flex-shrink: 0;
    }
    .list-item-pack-tag {
      font-size: 0.6rem;
      background: var(--color-surface-hover, #f0f4ff);
      color: var(--color-primary, #1a73e8);
      padding: 1px 5px;
      border-radius: 8px;
      white-space: nowrap;
    }
  `;

  @property({ type: String }) sessionKey = '';
  @property({ type: String }) masterDB = '';
  @property({ type: String }) transDB = '';
  /** Search only stores where the user holds a non-customer/non-supplier role. */
  @property({ type: Boolean }) registeredOnly = false;

  @state() private _query = '';
  @state() private _loading = false;
  @state() private _searched = false;
  @state() private _rows: SearchRow[] = [];
  @state() private _totalRows = 0;
  @state() private _totalStores = 0;
  @state() private _message = '';
  @state() private _uploading = new Set<string>(); // "storeCode:stockId" keys
  @state() private _deleting = new Set<string>();   // "storeCode:stockId:imageNo" keys
  @state() private _viewMode: 'tile' | 'list' = 'tile';
  @state() private _sortField: SortField = 'name';
  @state() private _sortDirection: 'asc' | 'desc' = 'asc';

  // ── Autocomplete / search history (persisted in localStorage) ──
  private static STORAGE_KEY = 'ppos_search_history';
  private static MAX_HISTORY = 50;
  @state() private _searchHistory: string[] = [];
  @state() private _suggestions: string[] = [];
  @state() private _showSuggestions = false;
  @state() private _selectedSuggestionIndex = -1;
  private _blurTimer: ReturnType<typeof setTimeout> | null = null;

  // Cart integration — track which items are already in cart
  @state() private _cartItems = new Set<string>();

  // Carousel state — per-row current slide index
  private _carouselIndex = new Map<string, number>();
  private _carouselTimers = new Map<string, ReturnType<typeof setInterval>>();
  private static CAROUSEL_INTERVAL = 4000; // ms between auto-flips

  // Live server events (auto-refresh of displayed results)
  private _eventUnsubs: Array<() => void> = [];
  private _refreshSoon = debounce(() => {
    if (this.isConnected && this._searched && this._query.trim()) void this._doSearch();
  }, 1000);

  private _uploadKey(storeCode: string, stockId: string): string {
    return `${storeCode}:${stockId}`;
  }

  private _rowKey(row: SearchRow): string {
    return `${row.storeCode}:${row.id}`;
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadHistory();
    this._loadCartState();
    window.addEventListener('cart-changed', this._onCartChanged);
    this._eventUnsubs = [
      serverEvents.on('stock', this._onServerEvent),
      serverEvents.on('purchase', this._onServerEvent),
      serverEvents.on('purchaseHold', this._onServerEvent),
      serverEvents.on('salesHold', this._onServerEvent),
      serverEvents.onLifecycle(this._onServerLifecycle),
    ];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('cart-changed', this._onCartChanged);
    this._refreshSoon.cancel();
    this._eventUnsubs.forEach((fn) => fn());
    this._eventUnsubs = [];
    // Clear all carousel timers
    for (const timer of this._carouselTimers.values()) {
      clearInterval(timer);
    }
    this._carouselTimers.clear();
    // Clear blur timer
    if (this._blurTimer) {
      clearTimeout(this._blurTimer);
      this._blurTimer = null;
    }
  }

  // ── Live server events (auto-refresh) ─────

  /**
   * Refresh the search only when the event can affect what is displayed:
   *  - the changed record id is among the current results, or
   *  - the list composition may have changed (insert/delete/undelete) for a
   *    store already present in the results.
   */
  private _onServerEvent = (ev: ServerEvent) => {
    if (!this._searched || !this._query.trim()) return;
    const affectsRow = this._rows.some((r) => r.storeCode === ev.store && String(r.id) === ev.value);
    if (affectsRow) {
      this._refreshSoon();
      return;
    }
    const listChange = ev.code === 'inserted' || ev.code === 'deleted' ||
                       ev.code === 'undeleted' || ev.code === 'updated-list';
    const knownStore = this._rows.some((r) => r.storeCode === ev.store);
    if (listChange && knownStore) this._refreshSoon();
  };

  /** Connection (re)established — results may have gone stale. */
  private _onServerLifecycle = (ev: { event: string }) => {
    if (ev.event === 'resync' && this._searched && this._query.trim()) this._refreshSoon();
  };

  /** Re-execute search when the selected store/database changes. */
  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('masterDB') || changedProperties.has('transDB') ||
        changedProperties.has('registeredOnly')) {
      if (this._searched && this._query.trim()) {
        this._doSearch();
      }
    }
  }

  private _startCarouselTimer(row: SearchRow) {
    const key = this._rowKey(row);
    this._stopCarouselTimer(key);
    const timer = setInterval(() => this._nextSlide(key, row.allImageUrls.length), StockSearch.CAROUSEL_INTERVAL);
    this._carouselTimers.set(key, timer);
  }

  private _stopCarouselTimer(key: string) {
    const existing = this._carouselTimers.get(key);
    if (existing) {
      clearInterval(existing);
      this._carouselTimers.delete(key);
    }
  }

  private _nextSlide(key: string, count: number) {
    if (count <= 1) return;
    const current = this._carouselIndex.get(key) || 0;
    this._carouselIndex.set(key, (current + 1) % count);
    this.requestUpdate();
  }

  private _goToSlide(key: string, index: number) {
    this._carouselIndex.set(key, index);
    // Reset timer on manual interaction
    this.requestUpdate();
    // Recreate the timer for this row — find the row by key
    const row = this._rows.find(r => this._rowKey(r) === key);
    if (row) this._startCarouselTimer(row);
  }

  private _loadHistory() {
    try {
      const raw = localStorage.getItem(StockSearch.STORAGE_KEY);
      this._searchHistory = raw ? JSON.parse(raw) : [];
    } catch {
      this._searchHistory = [];
    }
  }

  private _saveToHistory(query: string) {
    const q = query.trim();
    if (!q) return;
    // Remove duplicate if exists, then prepend
    const filtered = this._searchHistory.filter((h) => h.toLowerCase() !== q.toLowerCase());
    filtered.unshift(q);
    // Cap size
    if (filtered.length > StockSearch.MAX_HISTORY) {
      filtered.length = StockSearch.MAX_HISTORY;
    }
    this._searchHistory = filtered;
    try {
      localStorage.setItem(StockSearch.STORAGE_KEY, JSON.stringify(filtered));
    } catch {
      // localStorage may be full or unavailable — silently ignore
    }
  }

  // ── Cart integration ──────────────────────────────────────────────────
  private _onCartChanged = () => this._loadCartState();

  private _loadCartState() {
    try {
      const raw = localStorage.getItem('ppos_cart');
      if (!raw) { this._cartItems = new Set(); return; }
      const all: Array<{ stockId: string }> = JSON.parse(raw);
      this._cartItems = new Set(all.map(i => i.stockId));
    } catch { this._cartItems = new Set(); }
  }

  private _addToCart(row: SearchRow) {
    const storeCode = row.storeCode;
    const item = {
      stockId: row.id, name: row.name || row.code, code: row.code,
      price: row.price,
      packTypeId: row.packRecords?.[0]?.pack_type?.code || '',
      packDesc: row.packRecords?.[0]?.pack_description || '',
      quantity: 1, storeCode
    };
    try {
      const raw = localStorage.getItem('ppos_cart');
      let all: typeof item[] = raw ? JSON.parse(raw) : [];
      all = all.filter(i => i.storeCode === storeCode);
      // Don't duplicate
      if (!all.find(i => i.stockId === row.id)) {
        all.push(item);
      }
      localStorage.setItem('ppos_cart', JSON.stringify(all));
      this._cartItems = new Set(all.map(i => i.stockId));
      this.dispatchEvent(new CustomEvent('cart-changed', { bubbles: true, composed: true, detail: { stockId: row.id } }));
    } catch { /* ignore */ }
  }

  private _filterSuggestions(input: string) {
    const needle = input.trim().toLowerCase();
    if (!needle) {
      this._suggestions = this._searchHistory.slice(0, 8);
    } else {
      this._suggestions = this._searchHistory
        .filter((h) => h.toLowerCase().includes(needle))
        .slice(0, 8);
    }
    this._selectedSuggestionIndex = -1;
    this._showSuggestions = this._suggestions.length > 0;
  }

  private _selectSuggestion(query: string) {
    this._query = query;
    this._showSuggestions = false;
    this._selectedSuggestionIndex = -1;
    this._doSearch();
  }

  private _hideSuggestionsDelayed() {
    // Small delay so click on a suggestion fires before blur hides the list
    this._blurTimer = setTimeout(() => {
      this._showSuggestions = false;
      this._selectedSuggestionIndex = -1;
      this._blurTimer = null;
    }, 150);
  }

  private _handleSearchKeydown(e: KeyboardEvent) {
    if (!this._showSuggestions || this._suggestions.length === 0) {
      if (e.key === 'Enter') {
        this._doSearch();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._selectedSuggestionIndex =
        (this._selectedSuggestionIndex + 1) % this._suggestions.length;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._selectedSuggestionIndex =
        this._selectedSuggestionIndex <= 0
          ? this._suggestions.length - 1
          : this._selectedSuggestionIndex - 1;
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (this._selectedSuggestionIndex >= 0 && this._selectedSuggestionIndex < this._suggestions.length) {
        this._selectSuggestion(this._suggestions[this._selectedSuggestionIndex]);
      } else {
        this._showSuggestions = false;
        this._doSearch();
      }
    } else if (e.key === 'Escape') {
      this._showSuggestions = false;
      this._selectedSuggestionIndex = -1;
    }
  }

  private async _doSearch() {
    const q = this._query.trim();
    if (!q) return;

    this._showSuggestions = false;
    this._loading = true;
    this._searched = true;
    this._rows = [];
    this._message = '';
    // Clear existing carousel state
    for (const timer of this._carouselTimers.values()) clearInterval(timer);
    this._carouselTimers.clear();
    this._carouselIndex.clear();
    try {
      const result = await api.searchStock(q, this.masterDB || undefined, this.transDB || undefined, this.registeredOnly);
      this._rows = result.data || [];
      this._totalRows = result.totalRows;
      this._totalStores = result.totalStores;
      this._message = result.message || '';
      // Start carousel timers for rows with multiple images
      for (const row of this._rows) {
        if (row.allImageUrls && row.allImageUrls.length > 1) {
          this._startCarouselTimer(row);
        }
      }
      // Save successful search query to history
      this._saveToHistory(q);
    } catch (err: any) {
      console.error('Search failed:', err);
      // Surface the failure instead of leaving a silent empty list
      // (e.g. gateway session dropped mid-shift).
      this._message = err?.message || 'Search failed — please try again.';
    } finally {
      this._loading = false;
    }
  }

  private _renderCarousel(row: SearchRow) {
    const key = this._rowKey(row);
    const isUploading = this._uploading.has(this._uploadKey(row.storeCode, row.id));
    const entries = row.allImageUrls && row.allImageUrls.length > 0
      ? row.allImageUrls
      : (row.thumbnailUrl || row.imageUrl
          ? [{ url: row.thumbnailUrl || row.imageUrl, imageNo: 0 }]
          : []);
    const count = entries.length;
    const current = count > 0 ? (this._carouselIndex.get(key) || 0) : 0;

    // Start timer on first render if needed
    if (count > 1 && !this._carouselTimers.has(key)) {
      this._startCarouselTimer(row);
    }

    const slides = count > 0
      ? entries.map((entry, i) => html`
          <div class="carousel-slide">
            <img class="carousel-img" src=${entry.url} alt="${row.name} (${i + 1}/${count})" loading="lazy" />
          </div>
        `)
      : html`<div class="carousel-slide"><div class="carousel-placeholder">No image</div></div>`;

    return html`
      <div class="carousel"
           @mouseenter=${() => this._stopCarouselTimer(key)}
           @mouseleave=${() => count > 1 && this._startCarouselTimer(row)}>
        <div class="carousel-track" style="transform: translateX(-${current * 100}%)">
          ${slides}
        </div>
        ${count > 1 ? html`
          <div class="carousel-counter">${current + 1}/${count}</div>
          <div class="carousel-dots">
            ${entries.map((_, i) => html`
              <button class="carousel-dot ${i === current ? 'active' : ''}"
                      @click=${(e: Event) => { e.stopPropagation(); this._goToSlide(key, i); }}
                      aria-label="Image ${i + 1} of ${count}"></button>
            `)}
          </div>
        ` : ''}
        ${isUploading ? html`<div class="tile-upload-spinner"></div>` : ''}
        ${count > 0 ? html`
          <button
            class="tile-delete-btn"
            title="Delete this image"
            ?disabled=${this._deleting.has(`${this._uploadKey(row.storeCode, row.id)}:${entries[current]?.imageNo}`)}
            @click=${(e: Event) => this._deleteImage(row, current, e)}
          >✕</button>
        ` : ''}
        <button
          class="tile-upload-btn"
          title="Upload image"
          ?disabled=${isUploading}
          @click=${(e: Event) => { e.stopPropagation(); this._triggerUpload(row); }}
        >+</button>
      </div>
    `;
  }

  private _triggerUpload(row: SearchRow) {
    const input = this.shadowRoot?.getElementById('upload-input') as HTMLInputElement;
    if (!input) return;
    // Store the target row data on the element for the change handler
    (input as any).__uploadRow = row;
    input.click();
  }

  private async _onFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    const row = (input as any).__uploadRow as SearchRow | undefined;
    const file = input.files?.[0];
    input.value = ''; // reset so same file can be re-selected
    if (!row || !file) return;

    const key = this._uploadKey(row.storeCode, row.id);
    this._uploading = new Set(this._uploading).add(key);
    this.requestUpdate();

    try {
      const result = await api.uploadStockImage(row.storeCode, row.id, file);

      if (!result.success || !result.data) {
        throw new Error('Upload failed. You may need Admin access.');
      }

      // Refresh all image URLs from the server response so the carousel
      // shows all images immediately after upload.
      const ts = Date.now();
      const sk = this.sessionKey ? `sk=${encodeURIComponent(this.sessionKey)}&` : '';
      const allImgs = result.data.all_images || [];
      const base = `/api/images/${encodeURIComponent(row.storeCode)}/${encodeURIComponent(row.id)}`;

      row.allImageUrls = allImgs.map((img) => ({
        url: `${base}/${img.image_no}?${sk}thumb=1&fp=${encodeURIComponent(img.thumb_path || img.file_path)}&t=${ts}`,
        imageNo: img.image_no,
      }));

      // Keep backward-compat single-image URLs too
      const primary = allImgs.find((img) => img.image_no === 0);
      if (primary) {
        row.imageUrl = `${base}/0?${sk}fp=${encodeURIComponent(primary.file_path)}&t=${ts}`;
        row.thumbnailUrl = `${base}/0?${sk}thumb=1&fp=${encodeURIComponent(primary.thumb_path || primary.file_path)}&t=${ts}`;
      }

      // Start carousel timer if we now have multiple images
      if (row.allImageUrls.length > 1) {
        this._startCarouselTimer(row);
      }
    } catch (err: any) {
      console.error('Image upload failed:', err);
      alert(err.message || 'Upload failed. You may need Admin access.');
    } finally {
      const next = new Set(this._uploading);
      next.delete(key);
      this._uploading = next;
      this.requestUpdate();
    }
  }

  private async _deleteImage(row: SearchRow, carouselIndex: number, e: Event) {
    e.stopPropagation();
    const entry = row.allImageUrls[carouselIndex];
    if (!entry) return;

    if (!window.confirm(`Delete image ${carouselIndex + 1} of ${row.allImageUrls.length}?`)) return;

    const imageNo = entry.imageNo;
    console.log(`[carousel] Deleting imageNo=${imageNo} for ${row.storeCode}/${row.id} (carousel index ${carouselIndex})`);
    const deleteKey = `${this._uploadKey(row.storeCode, row.id)}:${imageNo}`;
    this._deleting = new Set(this._deleting).add(deleteKey);
    this.requestUpdate();
    try {
      await api.deleteStockImage(row.storeCode, row.id, imageNo);
      // Remove the deleted entry from allImageUrls and adjust carousel index
      row.allImageUrls = row.allImageUrls.filter((img) => img.imageNo !== imageNo);
      const rowKey = this._rowKey(row);
      const currentIdx = this._carouselIndex.get(rowKey) || 0;
      if (row.allImageUrls.length === 0) {
        row.imageUrl = '';
        row.thumbnailUrl = '';
        this._stopCarouselTimer(rowKey);
        this._carouselIndex.delete(rowKey);
      } else if (currentIdx >= row.allImageUrls.length) {
        this._carouselIndex.set(rowKey, Math.max(0, row.allImageUrls.length - 1));
      }
    } catch (err: any) {
      console.error('Image delete failed:', err);
      alert(err.message || 'Delete failed. You may need Admin access.');
    } finally {
      const next = new Set(this._deleting);
      next.delete(deleteKey);
      this._deleting = next;
      this.requestUpdate();
    }
  }

  private _renderPackTags(row: SearchRow) {
    if (!row.packRecords || row.packRecords.length <= 1) return html``;
    return html`
      <div class="tile-packs">
        ${row.packRecords.map((p) => html`
          <span class="tile-pack-tag">
            ${p.pack_description || p.pack_type?.name || 'Pack'} — RM ${formatPrice(p.selling_price)}
          </span>
        `)}
      </div>
    `;
  }

  private _sortRows(): SearchRow[] {
    const dir = this._sortDirection === 'asc' ? 1 : -1;
    const field = this._sortField;
    return [...this._rows].sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (field) {
        case 'code':
          va = a.code || '';
          vb = b.code || '';
          break;
        case 'name':
          va = (a.name || a.code || '').toLowerCase();
          vb = (b.name || b.code || '').toLowerCase();
          break;
        case 'price':
          va = a.price;
          vb = b.price;
          break;
        case 'qoh':
          va = a.qoh;
          vb = b.qoh;
          break;
        case 'store':
          va = (a.storeName || '').toLowerCase();
          vb = (b.storeName || '').toLowerCase();
          break;
        default:
          return 0;
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }

  private _handleSort(field: SortField) {
    if (this._sortField === field) {
      this._sortDirection = this._sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this._sortField = field;
      this._sortDirection = 'asc';
    }
  }

  private _renderSortArrow(field: SortField) {
    const isActive = this._sortField === field;
    const isAsc = this._sortDirection === 'asc';
    return html`
      <span class="sort-arrow ${isActive && isAsc ? 'asc' : ''}">▲</span>
      <span class="sort-arrow ${isActive && !isAsc ? 'desc' : ''}">▼</span>
    `;
  }

  private _renderListView() {
    const sorted = this._sortRows();
    return html`
      <div class="list-view">
        <div class="list-header">
          <div class="list-header-cell sortable list-header-code ${this._sortField === 'code' ? 'active' : ''}"
               @click=${() => this._handleSort('code')}>
            Code ${this._renderSortArrow('code')}
          </div>
          <div class="list-header-cell sortable list-header-name ${this._sortField === 'name' ? 'active' : ''}"
               @click=${() => this._handleSort('name')}>
            Name ${this._renderSortArrow('name')}
          </div>
          <div class="list-header-cell sortable list-header-price ${this._sortField === 'price' ? 'active' : ''}"
               @click=${() => this._handleSort('price')}>
            Price ${this._renderSortArrow('price')}
          </div>
          <div class="list-header-cell sortable list-header-qoh ${this._sortField === 'qoh' ? 'active' : ''}"
               @click=${() => this._handleSort('qoh')}>
            QOH ${this._renderSortArrow('qoh')}
          </div>
          <div class="list-header-cell sortable list-header-store ${this._sortField === 'store' ? 'active' : ''}"
               @click=${() => this._handleSort('store')}>
            Store ${this._renderSortArrow('store')}
          </div>
        </div>
        ${sorted.map((row) => html`
          <div class="list-item">
            <div class="list-item-code" title=${row.code}>${row.code || '—'}</div>
            <div class="list-item-name" title=${row.name}>${row.name || row.code}</div>
            ${row.price > 0 ? html`
              <div class="list-item-price">RM ${formatPrice(row.price)}</div>
            ` : html`<div class="list-item-price"></div>`}
            <div class="list-item-qoh">${row.qohString || row.qoh}</div>
            <div class="list-item-store" title=${row.storeName}>${row.storeName}</div>
            ${row.packRecords && row.packRecords.length > 1 ? html`
              <div class="list-item-packs">
                ${row.packRecords.map((p) => html`
                  <span class="list-item-pack-tag">${p.pack_description || p.pack_type?.name || 'Pack'}</span>
                `)}
              </div>
            ` : ''}
          </div>
        `)}
      </div>
    `;
  }

  render() {
    return html`
      <input
        id="upload-input"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        @change=${this._onFileSelected}
      />
      <div class="search-fixed">
        <div class="search-bar">
          <div class="search-bar-wrapper">
          <input
            type="text"
            placeholder="Search stock items..."
            .value=${this._query}
            @input=${(e: InputEvent) => {
              this._query = (e.target as HTMLInputElement).value;
              this._filterSuggestions(this._query);
            }}
            @focus=${() => this._filterSuggestions(this._query)}
            @blur=${() => this._hideSuggestionsDelayed()}
            @keydown=${this._handleSearchKeydown}
          />
          ${this._showSuggestions && this._suggestions.length > 0 ? html`
            <div class="suggestions-dropdown" @mousedown=${(e: Event) => e.preventDefault()}>
              ${this._suggestions.map((s, i) => html`
                <div
                  class="suggestion-item ${i === this._selectedSuggestionIndex ? 'active' : ''}"
                  @click=${() => this._selectSuggestion(s)}
                  @mouseenter=${() => this._selectedSuggestionIndex = i}
                >
                  <svg class="suggestion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <span class="suggestion-text">${s}</span>
                  <span class="suggestion-hint">history</span>
                </div>
              `)}
            </div>
          ` : ''}
        </div>
        <button class="view-toggle" title="Toggle tile / list view" @click=${() => this._viewMode = this._viewMode === 'tile' ? 'list' : 'tile'}>
          ${this._viewMode === 'tile' ? html`
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
            List
          ` : html`
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
            </svg>
            Tile
          `}
        </button>
        <button class="btn" ?disabled=${this._loading} @click=${this._doSearch}>
          ${this._loading ? 'Searching...' : 'Search'}
        </button>
      </div>
      </div>

      ${this._loading ? html`
        <div style="padding:2rem;text-align:center;color:var(--color-text-secondary)">
          Searching...
        </div>
      ` : this._searched ? html`
        <div class="search-fixed">
          <div class="summary">
            ${this._totalRows} item(s) across ${this._totalStores} store(s)
          </div>
        </div>
        <div class="results-scroll">
        ${this._rows.length > 0 && this._viewMode === 'tile' ? html`
          <div class="tile-grid">
            ${this._rows.map((row) => html`
              <div class="tile">
                ${this._renderCarousel(row)}
                <div class="tile-body">
                  <div class="tile-name">${row.name || row.code}</div>
                  ${row.code && row.code !== row.name ? html`
                    <div class="tile-code">${row.code}</div>
                  ` : ''}
                  <div class="tile-details">
                    ${row.price > 0 ? html`
                      <div class="tile-detail">
                        <span class="tile-detail-label">Price</span>
                        <span class="tile-detail-value tile-price">RM ${formatPrice(row.price)}</span>
                      </div>
                    ` : ''}
                    <div class="tile-detail">
                      <span class="tile-detail-label">QOH</span>
                      <span class="tile-detail-value">${row.qohString || row.qoh}</span>
                    </div>
                  </div>
                  ${this._renderPackTags(row)}
                  <div class="tile-meta">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    ${row.storeName}
                  </div>
                  ${this.masterDB || this.transDB ? html`
                    <button class="btn-add-cart" @click=${() => this._addToCart(row)}
                      ?disabled=${this._cartItems.has(row.id)}>
                      ${this._cartItems.has(row.id) ? '✓ In Cart' : '＋ Cart'}
                    </button>
                  ` : ''}
                </div>
              </div>
            `)}
          </div>
        ` : this._rows.length > 0 && this._viewMode === 'list' ? html`
          ${this._renderListView()}
        ` : html`
          <div class="no-results">
            ${this._message || 'No record found.'}
          </div>
        `}
        </div>
      ` : ''}
    `;
  }
}
